import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, AlertTriangle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
    getNotificationInbox,
    getUnreadNotificationCount,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    type InboxNotification,
} from '@/services/api/notification-inbox';

const UNREAD_COUNT_QUERY_KEY = ['notification-unread-count'];
const INBOX_QUERY_KEY = ['notification-inbox'];

/** Tempo relativo simples a partir de Date.now() — sem ajuste manual de fuso. */
function relativeTime(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'agora';
    if (minutes < 60) return `há ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `há ${hours} h`;
    const days = Math.floor(hours / 24);
    return `há ${days} d`;
}

interface NotificationItemProps {
    notification: InboxNotification;
    onRead: (id: number) => void;
}

function NotificationItem({ notification, onRead }: NotificationItemProps) {
    const isFailed = notification.status === 'FAILED';

    const handleClick = () => {
        if (!notification.is_read) {
            onRead(notification.id);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className={cn(
                'flex w-full items-start gap-2 rounded-md px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent',
                !notification.is_read && 'bg-muted'
            )}
        >
            <span
                aria-hidden="true"
                className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', notification.is_read ? 'bg-transparent' : 'bg-primary')}
            />
            <span className="min-w-0 flex-1">
                <span className="flex items-start justify-between gap-2">
                    <span className="line-clamp-2 text-foreground">{notification.log_message}</span>
                    {isFailed && (
                        <AlertTriangle
                            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive"
                            aria-label="Não entregue"
                        />
                    )}
                </span>
                <span className="mt-0.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{notification.project_name ?? "Sem projeto"}</span>
                    <span className="shrink-0">{relativeTime(notification.sent_at ?? notification.created_at)}</span>
                </span>
            </span>
        </button>
    );
}

/** Sino de notificações do cabeçalho: mostra a contagem de não lidas e a lista das mais recentes. */
export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const queryClient = useQueryClient();

    const { data: unreadCount } = useQuery({
        queryKey: UNREAD_COUNT_QUERY_KEY,
        queryFn: getUnreadNotificationCount,
        refetchInterval: 60_000,
        retry: false,
    });

    const {
        data: notifications,
        isLoading,
        isError,
    } = useQuery({
        queryKey: INBOX_QUERY_KEY,
        queryFn: () => getNotificationInbox({ limit: 20 }),
        enabled: open,
        retry: false,
    });

    const invalidateNotificationQueries = () => {
        void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
        void queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
    };

    const markReadMutation = useMutation({
        mutationFn: markNotificationAsRead,
        onSuccess: invalidateNotificationQueries,
    });

    const markAllReadMutation = useMutation({
        mutationFn: markAllNotificationsAsRead,
        onSuccess: invalidateNotificationQueries,
    });

    const count = unreadCount ?? 0;
    const badgeLabel = count > 99 ? '99+' : String(count);
    const hasUnread = notifications?.some((notification) => !notification.is_read) ?? false;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label="Notificações"
                    className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <Bell className="h-4 w-4" />
                    {count > 0 && (
                        <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
                            {badgeLabel}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
                    <p className="text-sm font-semibold text-foreground">Notificações</p>
                    {hasUnread && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => markAllReadMutation.mutate()}
                            disabled={markAllReadMutation.isPending}
                        >
                            Marcar todas como lidas
                        </Button>
                    )}
                </div>

                <div className="max-h-96 overflow-y-auto p-1">
                    {isLoading && <p className="px-3 py-8 text-center text-sm text-muted-foreground">Carregando...</p>}
                    {isError && (
                        <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                            Não foi possível carregar as notificações.
                        </p>
                    )}
                    {!isLoading && !isError && notifications?.length === 0 && (
                        <p className="px-3 py-8 text-center text-sm text-muted-foreground">Nenhuma notificação</p>
                    )}
                    {!isLoading &&
                        !isError &&
                        notifications?.map((notification) => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onRead={(id) => markReadMutation.mutate(id)}
                            />
                        ))}
                </div>

                <div className="border-t border-border px-3 py-2">
                    <Link
                        to="/notifications"
                        onClick={() => setOpen(false)}
                        className="block text-center text-xs text-muted-foreground hover:text-foreground"
                    >
                        Ver todas
                    </Link>
                </div>
            </PopoverContent>
        </Popover>
    );
}
